import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { fetchWithAuth } from '../../lib/api';
import { Link } from 'react-router-dom';
import {
  Megaphone, Ticket, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, RotateCcw, ArrowRight
} from 'lucide-react';

const TX_META = {
  credit_granted:  { icon: ArrowUpRight,   color: 'text-emerald-600 bg-emerald-50', label: 'Verildi' },
  credit_spent:    { icon: ArrowDownRight, color: 'text-rose-600 bg-rose-50',       label: 'Kullanıldı' },
  credit_refunded: { icon: RotateCcw,      color: 'text-sky-600 bg-sky-50',         label: 'İade' },
};

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const MiniStat = ({ icon: Icon, label, value, tone, testid }) => {
  const TONE_BG = {
    violet: 'bg-violet-100 text-violet-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    rose: 'bg-rose-100 text-rose-700',
  }[tone] || 'bg-slate-100 text-slate-700';
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-xl">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${TONE_BG}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-slate-500 leading-tight truncate">{label}</p>
        <p className="text-base font-bold text-slate-800 leading-tight mt-0.5" data-testid={testid}>
          {value === null || value === undefined ? <span className="text-xs text-slate-400 font-normal">Veri yok</span> : value.toLocaleString('tr-TR')}
        </p>
      </div>
    </div>
  );
};

export const CreditCampaignSummary = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchSummary = async () => {
      try {
        const d = await fetchWithAuth('/superadmin/activity-feed/credit-campaign-summary');
        if (alive) setData(d);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchSummary();
    const id = setInterval(fetchSummary, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <Card className="border-0 shadow-lg" data-testid="credit-campaign-summary">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-violet-600" />
            Kredi &amp; Kampanya
          </CardTitle>
          <Link
            to="/superadmin/audit-log?credit_only=true"
            className="text-xs text-[#5A67D8] hover:underline flex items-center gap-1"
            data-testid="credit-summary-view-all"
          >
            Tümünü Gör <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <p className="text-xs text-slate-500 mt-1">30 saniyede bir güncellenir</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mini stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <MiniStat
            icon={Megaphone}
            label="Aktif Kampanya"
            value={loading ? null : data?.active_campaigns}
            tone="violet"
            testid="cc-active-campaigns"
          />
          <MiniStat
            icon={Ticket}
            label="Toplam Promo Kod"
            value={loading ? null : data?.total_promo_codes}
            tone="indigo"
            testid="cc-total-promo"
          />
          <MiniStat
            icon={TrendingUp}
            label="Bugün Kredi Verildi"
            value={loading ? null : data?.credits_granted_today}
            tone="emerald"
            testid="cc-credits-granted"
          />
          <MiniStat
            icon={TrendingDown}
            label="Bugün Kredi Kullanıldı"
            value={loading ? null : data?.credits_spent_today}
            tone="rose"
            testid="cc-credits-spent"
          />
        </div>

        {/* Recent transactions */}
        <div>
          <p className="text-xs font-semibold text-slate-700 mb-2">Son Kredi Hareketleri</p>
          {loading ? (
            <div className="text-xs text-slate-400 py-4 text-center">Yükleniyor...</div>
          ) : !data?.recent_transactions || data.recent_transactions.length === 0 ? (
            <div className="text-xs text-slate-400 py-4 text-center" data-testid="cc-tx-empty">Henüz hareket yok</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="cc-tx-list">
              {data.recent_transactions.slice(0, 6).map((t) => {
                const meta = TX_META[t.type] || { icon: ArrowUpRight, color: 'text-slate-500 bg-slate-50', label: t.type };
                const Icon = meta.icon;
                return (
                  <li key={t.id} className="flex items-center gap-3 py-2.5" data-testid={`cc-tx-${t.type}`}>
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs leading-tight text-slate-700">
                        <span className="font-medium">{t.actor_name}</span>
                        {t.actor_name !== t.target_name && (
                          <> → <span className="text-slate-500">{t.target_name}</span></>
                        )}
                        <span className="text-slate-500"> · {t.amount} kredi {meta.label.toLowerCase()}</span>
                        {t.feature_key && <span className="text-slate-400"> ({t.feature_key})</span>}
                      </p>
                    </div>
                    <span className="text-[11px] text-slate-400 flex-shrink-0">{fmtDate(t.created_at)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CreditCampaignSummary;
